/**
 * /particles - the flood in three dimensions.
 *
 * The terrain is the source's own DEM as a lit mesh, draped with Esri World
 * Imagery, casting shadows from a sun placed at the site's real latitude. The
 * flood is a second mesh at ground + depth on the same grid, with a skirt at its
 * edge, morphing between the stored frames. Both are drawn at the vertical
 * exaggeration shown on the canvas at all times.
 *
 * The model shown is the one selected for this run on the Comparison page, or
 * the run's own result. All data: GET /api/scene3d/* (backend/app/routers/scene3d.py).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import DeckGL, { type DeckGLRef } from '@deck.gl/react'
import {
  AmbientLight,
  COORDINATE_SYSTEM,
  DirectionalLight,
  FlyToInterpolator,
  LightingEffect,
  MapView,
  WebMercatorViewport,
  _SunLight as SunLight,
} from '@deck.gl/core'
import { ScatterplotLayer, TextLayer } from '@deck.gl/layers'
import { Pause, Play, RotateCcw } from 'lucide-react'

import { api } from '../api/client'
import { CollapsibleLegend, CornerChrome } from '../components/MapCorner'
import { Playback } from '../components/FloodMap'
import type { Run } from '../api/types'
import { WATER_3D, WATER_3D_RAMP, cssColour, rampRgba } from '../components/Legend'
import { C } from '../design/tokens'
import { useAppStore } from '../store/useAppStore'
import {
  cellLonLat,
  clock,
  decodeFrame,
  decodeTerrain,
  lonLatCell,
  b64f32,
  type Frame,
  type FrameWire,
  type SceneMeta,
  type Terrain,
  type TerrainWire,
} from './scene3d/data'
import { IMAGERY_ATTRIBUTION, loadImagery, texCoordFor, type Imagery } from './scene3d/imagery'
import { TerrainSurfaceLayer, WaterSurfaceLayer, type SceneUniforms } from './scene3d/layers'
import { buildGroundPlane, buildTerrain, buildWater, castShadows, makePlan, withShade } from './scene3d/mesh'
import './scene3d/scene3d.css'

const SPEEDS = [0.5, 1, 2, 4] as const
/** Wall-clock seconds per stored frame at 1x: the Inundation page's clock (Playback.FPS). */
const SECONDS_PER_FRAME = 1 / Playback.FPS
const WET_M = 0.05
/** How long playback waits at a frame boundary for the next frames before going on without them. */
const HOLD_MS = 1200
/** Flat Deccan terrain disappears at 1:1; steep Himalayan terrain looks absurd above ~2x. */
const DEFAULT_EXAGGERATION: Record<string, number> = { UJJANI: 3.0, SIKKIM_GLOF: 1.5 }
const PITCH = 58
/** Steep enough to read the relief, never so steep the horizon swallows the grid. */
const MAX_PITCH = 72
/** Drag orbits (with inertia), shift- or right-drag pans, the wheel zooms toward the cursor, damped. */
const CONTROLLER = { dragMode: 'rotate', inertia: 450, scrollZoom: { speed: 0.01, smooth: true }, keyboard: false, doubleClickZoom: true, touchRotate: true }
// The far plane stays close: the shadow map covers the view frustum, so a distant far plane blurs every shadow.
const VIEW = new MapView({ id: 'scene', farZMultiplier: 1.6, nearZMultiplier: 0.02 })

const hexRgb = (hex: string): [number, number, number] => {
  const h = hex.replace('#', '')
  return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16)) as [number, number, number]
}

/** Sun direction at the site, 21 March, 09:30 local solar time: deck.gl's own solar position (_SunLight). */
class SiteSunLight extends SunLight {
  constructor(lat: number, lon: number) {
    const timestamp = Date.UTC(2026, 2, 21, 9, 30) - (lon / 15) * 3600 * 1000
    // Cast shadows are traced on the CPU (mesh.ts castShadows) toward this direction: deck.gl's shadow map
    // spans the whole view frustum, and over a 160 km grid each of its texels was kilometres wide.
    super({ timestamp, color: [255, 246, 232], intensity: 1.55, _shadow: false })
    SunLight.prototype.getProjectedLight.call(this, {
      layer: { context: { viewport: { latitude: lat, longitude: lon } } },
    } as never)
  }
  getProjectedLight() {
    return this
  }
}

interface ViewState {
  longitude: number
  latitude: number
  zoom: number
  pitch: number
  bearing: number
  position: [number, number, number]
  maxPitch: number
  transitionDuration?: number
  transitionInterpolator?: FlyToInterpolator
  transitionEasing?: (t: number) => number
}

const easeInOut = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2)


export default function Particles() {
  const deckRef = useRef<DeckGLRef>(null)
  const queryClient = useQueryClient()
  const activeSiteId = useAppStore((s) => s.activeSiteId)
  const selectedRunId = useAppStore((s) => s.selectedRunId)
  const setSelectedRunId = useAppStore((s) => s.setSelectedRunId)
  const storeFrame = useAppStore((s) => s.frameIndex)
  const setStoreFrame = useAppStore((s) => s.setFrameIndex)

  // ------------------------------------------------------------ what is shown

  const runs = useQuery<Run[]>({ queryKey: ['runs'], queryFn: api.runs })
  const siteRuns = useMemo(
    () => (runs.data ?? []).filter((r) => r.status === 'done' && r.site_id === activeSiteId && r.solver !== 'dualsphysics'),
    [runs.data, activeSiteId],
  )
  const run = siteRuns.find((r) => r.id === selectedRunId) ?? siteRuns[0] ?? null
  const source = run ? (run.selected_model ?? `run:${run.id}`) : null
  const sharesInundationTime = source !== null && run !== null && source === `run:${run.id}` && run.id === selectedRunId

  const meta = useQuery<SceneMeta>({
    queryKey: ['scene3d-meta', source],
    queryFn: () => api.raw(`/scene3d/meta?source=${encodeURIComponent(source!)}`),
    enabled: Boolean(source),
    staleTime: Infinity,
  })
  const terrainQ = useQuery<Terrain & { demSource: string | null }>({
    queryKey: ['scene3d-terrain', source],
    queryFn: async () => {
      const w = await api.raw<TerrainWire>(`/scene3d/terrain?source=${encodeURIComponent(source!)}`)
      return { ...decodeTerrain(w), demSource: w.dem_source }
    },
    enabled: Boolean(source),
    staleTime: Infinity,
  })
  const arrival = useQuery<Float32Array>({
    queryKey: ['scene3d-arrival', source],
    queryFn: async () => b64f32((await api.raw<{ arrival_s_f32: string }>(`/scene3d/arrival?source=${encodeURIComponent(source!)}`)).arrival_s_f32),
    enabled: Boolean(source),
    staleTime: Infinity,
  })
  const m = meta.data
  const terrain = terrainQ.data ?? null
  const frames = m?.times_s.length ?? 0

  // ------------------------------------------------------------ controls

  const [exaggeration, setExaggeration] = useState(DEFAULT_EXAGGERATION[activeSiteId] ?? 3)
  useEffect(() => setExaggeration(DEFAULT_EXAGGERATION[activeSiteId] ?? 3), [activeSiteId])
  const [pointMode, setPointMode] = useState(false)
  const [speed, setSpeed] = useState<(typeof SPEEDS)[number]>(1)
  const [playing, setPlaying] = useState(false)
  const [playhead, setPlayhead] = useState(0)
  const playheadRef = useRef(0)
  const openedFor = useRef<string | null>(null)

  // Open on the Inundation page's frame when this is the same run, else on the largest extent.
  useEffect(() => {
    if (!m || openedFor.current === m.source) return
    openedFor.current = m.source
    const start = sharesInundationTime && storeFrame > 0 ? Math.min(storeFrame, frames - 1) : (m.peak_extent_frame ?? 0)
    playheadRef.current = start
    setPlayhead(start)
    setPlaying(false)
  }, [m, frames, sharesInundationTime, storeFrame])

  const k = Math.min(Math.floor(playhead), Math.max(0, frames - 1))
  const frameQuery = useCallback(
    (t: number) => ({
      queryKey: ['scene3d-frame', source, t],
      queryFn: async (): Promise<Frame> =>
        decodeFrame(await api.raw<FrameWire>(`/scene3d/frame?source=${encodeURIComponent(source!)}&t=${t}`)),
      staleTime: Infinity,
      gcTime: 60_000,
    }),
    [source],
  )
  const frameA = useQuery({ ...frameQuery(k), enabled: Boolean(source) && frames > 0, placeholderData: (p) => p })
  const kB = Math.min(k + 1, frames - 1)
  const frameB = useQuery({ ...frameQuery(kB), enabled: Boolean(source) && frames > 0 })
  // Prefetch the next two frames so playback never waits at a boundary it can see coming.
  useEffect(() => {
    if (!source || !frames) return
    for (const t of [k + 2, k + 3]) if (t < frames) void queryClient.prefetchQuery(frameQuery(t))
  }, [source, frames, k, frameQuery, queryClient])

  // ------------------------------------------------------------ geometry

  const plan = useMemo(() => (m ? makePlan(m) : null), [m])
  const [imagery, setImagery] = useState<Imagery | null | 'loading'>('loading')
  useEffect(() => {
    if (!m) return
    let live = true
    setImagery('loading')
    loadImagery(m.bounds_lonlat).then((img) => live && setImagery(img))
    return () => {
      live = false
    }
  }, [m])
  const imageryReady = imagery !== 'loading' && imagery !== null
  const terrainMesh = useMemo(
    () => (plan && terrain && imagery !== 'loading' ? buildTerrain(plan, terrain, imageryReady ? texCoordFor(imagery as Imagery) : null) : null),
    [plan, terrain, imagery, imageryReady],
  )
  const ground = useMemo(
    () => (m && terrainMesh ? buildGroundPlane(m.bounds_lonlat, terrainMesh.floor, 3) : null),
    [m, terrainMesh],
  )
  const sun = useMemo(() => {
    const lat = m?.breach?.lat ?? (m ? (m.bounds_lonlat[1] + m.bounds_lonlat[3]) / 2 : 20)
    const lon = m?.breach?.lon ?? (m ? (m.bounds_lonlat[0] + m.bounds_lonlat[2]) / 2 : 78)
    return new SiteSunLight(lat, lon)
  }, [m])
  // Cast shadows for the exaggeration in force, recomputed a moment after the slider settles.
  const [shadeK, setShadeK] = useState(exaggeration)
  useEffect(() => {
    const id = window.setTimeout(() => setShadeK(exaggeration), 150)
    return () => window.clearTimeout(id)
  }, [exaggeration])
  const shadeField = useMemo(
    () => (plan && terrainMesh ? castShadows(plan, terrainMesh.z, shadeK, sun.direction) : null),
    [plan, terrainMesh, shadeK, sun],
  )
  const shadedTerrain = useMemo(
    () => (terrainMesh && plan && shadeField ? withShade(terrainMesh, plan, shadeField) : terrainMesh),
    [terrainMesh, plan, shadeField],
  )
  const a = frameA.data && frameA.data.t === k ? frameA.data : null
  const b = frameB.data && frameB.data.t === kB ? frameB.data : null
  // While the frames for a new playhead position load, the surface already built stays on screen: dropping it
  // blanks the flood for a moment on every scrub.
  const lastWater = useRef<{ plan: unknown; mesh: ReturnType<typeof buildWater> } | null>(null)
  const water = useMemo(() => {
    if (!plan || !terrainMesh) return null
    if (!a) return lastWater.current?.plan === plan ? lastWater.current.mesh : null
    const t0 = performance.now()
    const built = buildWater(plan, terrainMesh.z, a, b ?? a, WET_M, shadeField)
    ;(window as unknown as { __scene3dWaterMs: number }).__scene3dWaterMs = performance.now() - t0
    lastWater.current = { plan, mesh: built }
    return built
  }, [plan, terrainMesh, a, b, shadeField])

  // ------------------------------------------------------------ camera

  const [size, setSize] = useState({ width: 1200, height: 800 })
  const mainRef = useRef<HTMLElement | null>(null)
  useEffect(() => {
    const el = mainRef.current
    if (!el) return undefined
    const ro = new ResizeObserver(() => setSize({ width: el.clientWidth, height: el.clientHeight }))
    ro.observe(el)
    setSize({ width: el.clientWidth, height: el.clientHeight })
    return () => ro.disconnect()
  }, [])

  const elevationAt = useCallback(
    (lon: number, lat: number) => {
      if (!m || !terrain) return 0
      const [row, col] = lonLatCell(m.lattice, lon, lat)
      const rr = Math.max(0, Math.min(m.height - 1, Math.round(row)))
      const cc = Math.max(0, Math.min(m.width - 1, Math.round(col)))
      const e = terrain.elevation[rr * m.width + cc]
      return Number.isFinite(e) ? e : terrain.p2
    },
    [m, terrain],
  )

  const bearing = m?.down_valley_bearing_deg ?? 0
  /**
   * The view that frames `bounds` on screen at this pitch and bearing: start from the flat fit, then
   * project the box's corners (on the terrain, exaggerated) through the pitched camera and correct zoom
   * and centre until they sit inside the padding.
   */
  const framed = useCallback(
    (bounds: [number, number, number, number], pitch = PITCH, viewBearing = bearing): ViewState | null => {
      if (!m || size.width < 50) return null
      const [w, s, e, n] = bounds
      const pad = Math.min(40, size.width / 24)
      const fitted = new WebMercatorViewport({ width: size.width, height: size.height }).fitBounds(
        [
          [w, s],
          [e, n],
        ],
        { padding: pad },
      )
      const corners: [number, number][] = [
        [w, s],
        [e, s],
        [e, n],
        [w, n],
        [(w + e) / 2, s],
        [(w + e) / 2, n],
        [w, (s + n) / 2],
        [e, (s + n) / 2],
      ]
      let v: ViewState = {
        longitude: (w + e) / 2,
        latitude: (s + n) / 2,
        zoom: fitted.zoom,
        pitch,
        bearing: viewBearing,
        position: [0, 0, elevationAt((w + e) / 2, (s + n) / 2) * exaggeration],
        maxPitch: MAX_PITCH,
      }
      const start = v
      for (let it = 0; it < 14; it += 1) {
        const vp = new WebMercatorViewport({ ...v, width: size.width, height: size.height })
        const px = corners.map(([x, y]) => vp.project([x, y, elevationAt(x, y) * exaggeration]))
        if (px.some((q) => !Number.isFinite(q[0]) || !Number.isFinite(q[1]))) break
        const xs = px.map((q) => q[0])
        const ys = px.map((q) => q[1])
        const bw = Math.max(...xs) - Math.min(...xs)
        const bh = Math.max(...ys) - Math.min(...ys)
        const scale = Math.min((size.width - 2 * pad) / Math.max(bw, 1), (size.height - 2 * pad) / Math.max(bh, 1))
        const dz = Math.max(-1, Math.min(1, Math.log2(scale) * 0.8))
        // Recentre by at most a quarter of the screen per step, and never onto a point off the map.
        const dx = Math.max(-size.width / 4, Math.min(size.width / 4, (Math.max(...xs) + Math.min(...xs)) / 2 - size.width / 2))
        const dy = Math.max(-size.height / 4, Math.min(size.height / 4, (Math.max(...ys) + Math.min(...ys)) / 2 - size.height / 2))
        const [lon, lat] = vp.unproject([size.width / 2 + dx, size.height / 2 + dy], { targetZ: v.position[2] }) as number[]
        if (!Number.isFinite(lon) || !Number.isFinite(lat)) break
        v = { ...v, longitude: lon, latitude: lat, zoom: v.zoom + dz }
        v.position = [0, 0, elevationAt(lon, lat) * exaggeration]
        if (Math.abs(dz) < 0.02 && Math.abs(dx) + Math.abs(dy) < 4) break
      }
      // A fit that wandered outside the box it was asked to frame falls back to the flat fit.
      const inside = v.longitude >= w && v.longitude <= e && v.latitude >= s && v.latitude <= n
      return inside && Math.abs(v.zoom - start.zoom) < 3 ? v : start
    },
    [m, size, bearing, elevationAt, exaggeration],
  )

  /**
   * A box `cells` solver cells across, around a point and kept inside the run grid: a breach or town at the grid's
   * edge would otherwise frame half a screen of the grid's side wall.
   */
  const around = useCallback(
    (lon: number, lat: number, cells: number): [number, number, number, number] => {
      const half = ((m?.cell_m ?? 100) * cells) / 2
      const dLat = half / 110574
      const dLon = half / (111320 * Math.cos((lat * Math.PI) / 180))
      const [w, s, e, n] = m?.bounds_lonlat ?? [-180, -90, 180, 90]
      // slide the box inside the grid, keeping its size; only a box wider than the grid is cut
      const cx = Math.max(w + Math.min(dLon, (e - w) / 2), Math.min(e - Math.min(dLon, (e - w) / 2), lon))
      const cy = Math.max(s + Math.min(dLat, (n - s) / 2), Math.min(n - Math.min(dLat, (n - s) / 2), lat))
      return [Math.max(w, cx - dLon), Math.max(s, cy - dLat), Math.min(e, cx + dLon), Math.min(n, cy + dLat)]
    },
    [m],
  )

  const presets = useMemo(() => {
    if (!m) return []
    const out: { key: string; label: string; bounds: [number, number, number, number]; pitch: number }[] = []
    if (m.breach) out.push({ key: 'dam', label: 'Dam axis', bounds: around(m.breach.lon, m.breach.lat, Math.max(40, Math.min(m.width, m.height) / 2.5)), pitch: 60 })
    if (m.flood_bounds_lonlat) out.push({ key: 'reach', label: 'Downstream reach', bounds: m.flood_bounds_lonlat, pitch: PITCH })
    out.push({ key: 'basin', label: 'Full basin', bounds: m.bounds_lonlat, pitch: PITCH })
    for (const s of m.settlements.slice(0, 6)) {
      out.push({ key: `s:${s.name}`, label: s.name, bounds: around(s.lon, s.lat, Math.max(30, Math.min(m.width, m.height) / 4)), pitch: 58 })
    }
    return out
  }, [m, around])

  const [view, setView] = useState<ViewState | null>(null)
  const fittedFor = useRef<string | null>(null)
  /** Share of the canvas the run grid covers in a view (projected outline, on the terrain), 0 if it spills off. */
  const gridFill = useCallback(
    (v: ViewState) => {
      if (!m) return 0
      const [w, s, e, n] = m.bounds_lonlat
      const vp = new WebMercatorViewport({ ...v, width: size.width, height: size.height })
      const ring: [number, number][] = []
      for (let i = 0; i < 8; i += 1) ring.push([w + ((e - w) * i) / 8, s], [e, s + ((n - s) * i) / 8], [e - ((e - w) * i) / 8, n], [w, n - ((n - s) * i) / 8])
      const ordered = [0, 1, 2, 3].flatMap((side) => ring.filter((_, i) => i % 4 === side))
      const px = ordered.map(([x, y]) => vp.project([x, y, elevationAt(x, y) * exaggeration]))
      if (px.some(([x, y]) => !Number.isFinite(x) || x < -2 || y < -2 || x > size.width + 2 || y > size.height + 2)) return 0
      let area = 0
      for (let i = 0; i < px.length; i += 1) area += px[i][0] * px[(i + 1) % px.length][1] - px[(i + 1) % px.length][0] * px[i][1]
      return Math.abs(area) / 2 / (size.width * size.height)
    },
    [m, size, elevationAt, exaggeration],
  )
  /** Share of the screen that shows the run grid rather than its side walls or the plane around it (12 x 8 samples). */
  const coverage = useCallback(
    (v: ViewState) => {
      if (!m) return 0
      const [w, s, e, n] = m.bounds_lonlat
      const vp = new WebMercatorViewport({ ...v, width: size.width, height: size.height })
      let inside = 0
      for (let i = 0; i < 12; i += 1)
        for (let j = 0; j < 8; j += 1) {
          const [lon, lat] = vp.unproject([((i + 0.5) * size.width) / 12, ((j + 0.5) * size.height) / 8], { targetZ: v.position[2] }) as number[]
          if (lon >= w && lon <= e && lat >= s && lat <= n) inside += 1
        }
      return inside / 96
    },
    [m, size],
  )
  /** A close view of `bounds`, turned to the bearing that keeps the most of the screen on the grid. */
  const closeView = useCallback(
    (bounds: [number, number, number, number], pitch: number) => {
      let best: ViewState | null = null
      let bestScore = -1
      for (let turn = -180; turn < 180; turn += 30) {
        const v = framed(bounds, pitch, bearing + turn)
        if (!v) continue
        const score = coverage(v) * (1 + 0.08 * Math.cos((turn * Math.PI) / 180))
        if (score > bestScore) {
          best = v
          bestScore = score
        }
      }
      return best
    },
    [framed, coverage, bearing],
  )
  /**
   * The framed default: the whole run grid at the preset pitch, turned to whichever bearing within 90 degrees of
   * looking down-valley fills the canvas most. A long, narrow grid seen end-on leaves most of the frame empty.
   */
  const home = useCallback(() => {
    const p = presets.find((x) => x.key === 'basin')
    if (!p) return null
    let best: ViewState | null = null
    let bestScore = -1
    for (const pitch of [p.pitch, p.pitch - 8]) for (let turn = -90; turn <= 90; turn += 15) {
      const v = framed(p.bounds, pitch, bearing + turn)
      if (!v) continue
      // a steeper view reads the relief better, and looking down-valley reads the flow: both break near-ties
      const score = gridFill(v) * (1 + 0.08 * Math.cos((turn * Math.PI) / 180)) * (pitch === p.pitch ? 1.1 : 1)
      if (score > bestScore) {
        best = v
        bestScore = score
      }
    }
    return best
  }, [presets, framed, gridFill, bearing])
  /**
   * The bounds that keep the scene recoverable: altitude from one zoom level out of the framed default
   * down to one solver cell across 48 px; pitch 0 to MAX_PITCH; the orbit centre inside the run grid.
   */
  const homeZoom = useRef<number | null>(null)
  const clampView = useCallback(
    (v: ViewState): ViewState => {
      if (!m) return v
      const [w, s, e, n] = m.bounds_lonlat
      const lat = (s + n) / 2
      const maxZoom = Math.log2((40075016.686 * Math.cos((lat * Math.PI) / 180)) / (512 * (m.cell_m / 48)))
      const minZoom = Math.min(maxZoom - 1, (homeZoom.current ?? v.zoom) - 1)
      const longitude = Math.max(w, Math.min(e, v.longitude))
      const latitude = Math.max(s, Math.min(n, v.latitude))
      return {
        ...v,
        longitude,
        latitude,
        zoom: Math.max(minZoom, Math.min(maxZoom, v.zoom)),
        pitch: Math.max(0, Math.min(MAX_PITCH, v.pitch)),
        minZoom,
        maxZoom,
        minPitch: 0,
        maxPitch: MAX_PITCH,
      } as ViewState
    },
    [m],
  )
  const flyTo = useCallback(
    (next: ViewState | null, ms = 1600) => {
      if (!next) return
      setView({ ...clampView(next), transitionDuration: ms, transitionInterpolator: new FlyToInterpolator({ speed: 1.4 }), transitionEasing: easeInOut })
    },
    [clampView],
  )
  const reset = useCallback(() => {
    const target = home()
    if (!target) return
    homeZoom.current = target.zoom
    flyTo(target, 1000)
  }, [home, flyTo])
  const resetRef = useRef(reset)
  resetRef.current = reset

  // Fit once per source, when the terrain it stands on is known.
  useEffect(() => {
    if (!m || !terrain || size.width < 50 || fittedFor.current === m.source) return
    const target = home()
    if (!target) return
    fittedFor.current = m.source
    homeZoom.current = target.zoom
    flyTo(target, 1200)
  }, [m, terrain, size, home, flyTo])

  // A resized canvas keeps the framed default framed: refit when the size changes and the view is still home.
  const fittedSize = useRef('')
  useEffect(() => {
    if (!m || !terrain || size.width < 50) return
    const key = `${size.width}x${size.height}`
    if (fittedSize.current === '' || fittedSize.current === key) {
      fittedSize.current = key
      return
    }
    fittedSize.current = key
    const target = home()
    if (!target || homeZoom.current === null || !view) return
    if (Math.abs(view.zoom - homeZoom.current) < 0.05) {
      homeZoom.current = target.zoom
      setView(clampView(target))
    } else {
      homeZoom.current = target.zoom
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [size])

  // Keep the orbit centre on the ground when the exaggeration changes.
  useEffect(() => {
    setView((v) => (v ? { ...v, position: [0, 0, elevationAt(v.longitude, v.latitude) * exaggeration], transitionDuration: 240, transitionInterpolator: new FlyToInterpolator(), transitionEasing: easeInOut } : v))
  }, [exaggeration, elevationAt])

  // Keyboard: left / right orbit, up / down tilt, + / - zoom, R returns to the framed default.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName
      if (tag === 'SELECT' || tag === 'TEXTAREA' || (tag === 'INPUT' && (e.target as HTMLInputElement).type !== 'range')) return
      if (e.ctrlKey || e.metaKey || e.altKey) return
      if (e.code === 'KeyR') {
        e.preventDefault()
        resetRef.current()
        return
      }
      if (e.code === 'Space') {
        e.preventDefault()
        togglePlayRef.current()
        return
      }
      const delta: Partial<Record<string, Partial<ViewState>>> = {
        ArrowLeft: { bearing: -15 },
        ArrowRight: { bearing: 15 },
        ArrowUp: { pitch: 5 },
        ArrowDown: { pitch: -5 },
        Equal: { zoom: 0.5 },
        NumpadAdd: { zoom: 0.5 },
        Minus: { zoom: -0.5 },
        NumpadSubtract: { zoom: -0.5 },
      }
      const d = delta[e.code]
      if (!d) return
      if (tag === 'INPUT' && e.code.startsWith('Arrow')) return
      e.preventDefault()
      setView((v) =>
        v
          ? {
              ...clampRef.current({ ...v, bearing: v.bearing + (d.bearing ?? 0), pitch: v.pitch + (d.pitch ?? 0), zoom: v.zoom + (d.zoom ?? 0) }),
              transitionDuration: 300,
              transitionInterpolator: new FlyToInterpolator(),
              transitionEasing: easeInOut,
            }
          : v,
      )
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])
  const clampRef = useRef(clampView)
  clampRef.current = clampView

  const viewport = useMemo(
    () => (view ? new WebMercatorViewport({ ...view, width: size.width, height: size.height }) : null),
    [view, size],
  )

  // How much of the canvas the run grid fills, for the capture review (window.__scene3dFrame).
  useEffect(() => {
    if (!viewport || !m || !terrain) return
    const [w, s, e, n] = m.bounds_lonlat
    const ring: [number, number][] = []
    for (let i = 0; i < 16; i += 1) ring.push([w + ((e - w) * i) / 16, s])
    for (let i = 0; i < 16; i += 1) ring.push([e, s + ((n - s) * i) / 16])
    for (let i = 0; i < 16; i += 1) ring.push([e - ((e - w) * i) / 16, n])
    for (let i = 0; i < 16; i += 1) ring.push([w, n - ((n - s) * i) / 16])
    const px = ring.map(([x, y]) => viewport.project([x, y, elevationAt(x, y) * exaggeration]))
    const xs = px.map((q) => q[0])
    const ys = px.map((q) => q[1])
    let area = 0
    for (let i = 0; i < px.length; i += 1) {
      const [x1, y1] = px[i]
      const [x2, y2] = px[(i + 1) % px.length]
      area += x1 * y2 - x2 * y1
    }
    const bbox = [Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys)].map(Math.round)
    ;(window as unknown as { __scene3dFrame: unknown }).__scene3dFrame = {
      size: [size.width, size.height],
      bbox,
      inside: bbox[0] >= 0 && bbox[1] >= 0 && bbox[2] <= size.width && bbox[3] <= size.height,
      gridFill: Math.round((1000 * Math.abs(area)) / 2 / (size.width * size.height)) / 1000,
      view: { zoom: +view!.zoom.toFixed(2), pitch: +view!.pitch.toFixed(1), bearing: +view!.bearing.toFixed(1) },
    }
  }, [viewport, m, terrain, elevationAt, exaggeration, size, view])

  // ------------------------------------------------------------ lighting and uniforms

  const effects = useMemo(() => {
    const [dx, dy, dz] = sun.direction
    const fill = new DirectionalLight({ color: [236, 228, 216], intensity: 0.28, direction: [-dx, -dy, dz] })
    const lighting = new LightingEffect({ ambient: new AmbientLight({ color: [255, 255, 255], intensity: 0.9 }), sun, fill })
    return [lighting]
  }, [sun])

  const fog = useMemo(() => {
    if (!viewport) return { near: 1e9, far: 2e9 }
    const cam = viewport.cameraPosition
    const c = viewport.center
    const d = Math.hypot(cam[0] - c[0], cam[1] - c[1], cam[2] - c[2])
    return { near: d * 1.2, far: d * 5 }
  }, [viewport])

  const hasVectors = Boolean(m?.has_vectors && a?.u)
  const sceneRef = useRef<SceneUniforms>({
    exaggeration: 1,
    useTexture: 0,
    fogNear: 1e9,
    fogFar: 2e9,
    fogColor: hexRgb(C.skyHorizon).map((v) => v / 255) as [number, number, number],
    mixT: 0,
    time: 0,
    wetThreshold: WET_M,
    sink: 1,
    cellM: 100,
    hasVectors: 0,
    origin: [0, 0],
    metersPerDeg: [111320, 110574],
  })
  const scene = sceneRef.current
  scene.exaggeration = exaggeration
  scene.useTexture = imageryReady ? 1 : 0
  scene.fogNear = fog.near
  scene.fogFar = fog.far
  scene.sink = exaggeration
  scene.cellM = (m?.cell_m ?? 100) * (plan?.stride ?? 1)
  scene.hasVectors = hasVectors ? 1 : 0
  if (m) {
    const lat0 = (m.bounds_lonlat[1] + m.bounds_lonlat[3]) / 2
    scene.origin = [m.bounds_lonlat[0], m.bounds_lonlat[1]]
    scene.metersPerDeg = [111320 * Math.cos((lat0 * Math.PI) / 180), 110574]
  }

  // One loop drives time: the playhead while playing, the water's shimmer or flow always. It reads the
  // React state through refs, so it is started once and never re-subscribed mid-playback.
  const live = useRef({ playing, speed, frames, waterK: -1, waterHasB: false, scrubbing: false, holds: 0, skips: 0, holdSince: 0, lastAsk: 0, waiting: null as null | Record<string, boolean> })
  live.current.playing = playing
  live.current.speed = speed
  live.current.frames = frames
  useEffect(() => {
    if (!water) {
      live.current.waterK = -1
      live.current.waterHasB = false
    } else if (a) {
      live.current.waterK = a.t
      live.current.waterHasB = Boolean(b && b.t > a.t)
    }
  }, [water, a, b])
  useEffect(() => {
    let id = 0
    let last: number | null = null
    let lastUi = 0
    const tick = (now: number) => {
      const dt = last === null ? 0 : Math.max(0, Math.min(0.25, (now - last) / 1000))
      last = now
      const L = live.current
      const s = sceneRef.current
      s.time = now / 1000
      if (L.playing && !L.scrubbing && L.frames > 1) {
        const cur = playheadRef.current
        let next = cur + (dt * L.speed) / SECONDS_PER_FRAME
        const nextK = Math.floor(cur) + 1
        if (next >= L.frames - 1) {
          // stop on the last stored frame, as the Inundation page does
          playheadRef.current = L.frames - 1
          setPlayhead(L.frames - 1)
          setPlaying(false)
          L.playing = false
          id = requestAnimationFrame(tick)
          return
        }
        if (next >= nextK) {
          // hold at the boundary until the frames either side of it have arrived and been meshed
          const haveA = Boolean(queryClient.getQueryData(frameQuery(nextK).queryKey))
          const haveB = Boolean(queryClient.getQueryData(frameQuery(nextK + 1).queryKey))
          const meshed = L.waterK === Math.floor(cur)
          if (haveA && haveB && meshed) {
            L.holdSince = 0
          } else {
            // Wait for the frames, but never for long: a fetch that is refused or dropped once must not
            // freeze the clock. After HOLD_MS the playhead goes on, drawing the last surface built.
            if (!L.holdSince) L.holdSince = now
            if (now - L.holdSince < HOLD_MS) {
              next = nextK - 1e-6
              L.holds += 1
              L.waiting = { frameA: !haveA, frameB: !haveB, mesh: !meshed }
              // a fetch that failed or was collected is asked for again; an in-flight one is shared, not repeated
              if (now - L.lastAsk > 250) {
                L.lastAsk = now
                if (!haveA) void queryClient.prefetchQuery(frameQuery(nextK))
                if (!haveB) void queryClient.prefetchQuery(frameQuery(nextK + 1))
              }
            } else {
              L.holdSince = 0
              L.skips += 1
            }
          }
        }
        playheadRef.current = Math.max(0, next)
        if (Math.floor(next) !== Math.floor(cur) || now - lastUi > 100) {
          lastUi = now
          setPlayhead(playheadRef.current)
        }
      }
      const f = playheadRef.current - L.waterK
      s.mixT = L.waterHasB && f > 0 && f < 1 ? f : L.waterHasB && f >= 1 ? 1 : 0
      ;(window as unknown as { __scene3dPlay: unknown }).__scene3dPlay = { pos: playheadRef.current, playing: L.playing, holds: L.holds, skips: L.skips, waiting: L.waiting, mixT: s.mixT, waterK: L.waterK }
      deckRef.current?.deck?.redraw('scene3d-time')
      id = requestAnimationFrame(tick)
    }
    id = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(id)
  }, [queryClient, frameQuery])

  const togglePlay = useCallback(() => {
    setPlaying((p) => {
      if (p) {
        const r = Math.round(playheadRef.current)
        playheadRef.current = r
        setPlayhead(r)
        return false
      }
      if (playheadRef.current >= frames - 1) {
        playheadRef.current = 0
        setPlayhead(0)
      }
      return true
    })
  }, [frames])
  const togglePlayRef = useRef(togglePlay)
  togglePlayRef.current = togglePlay

  // Pausing or scrubbing on the run's own result moves the Inundation page's frame too.
  useEffect(() => {
    if (!playing && sharesInundationTime) setStoreFrame(Math.round(playhead))
  }, [playing, playhead, sharesInundationTime, setStoreFrame])

  // fps, measured from rendered frames (window.__scene3dFps), for the report
  useEffect(() => {
    let frames = 0
    let t0 = performance.now()
    let id = 0
    const loop = (now: number) => {
      frames += 1
      if (now - t0 >= 1000) {
        ;(window as unknown as { __scene3dFps: number }).__scene3dFps = (frames * 1000) / (now - t0)
        frames = 0
        t0 = now
      }
      id = requestAnimationFrame(loop)
    }
    id = requestAnimationFrame(loop)
    return () => cancelAnimationFrame(id)
  }, [])

  // ------------------------------------------------------------ labels

  const labels = useMemo(() => {
    if (!m || !terrain || !viewport) return []
    const items = [
      ...(m.breach ? [{ name: 'Breach', lon: m.breach.lon, lat: m.breach.lat, rank: Infinity }] : []),
      ...m.settlements.map((s) => ({ name: s.name, lon: s.lon, lat: s.lat, rank: s.population ?? 0 })),
    ].sort((p, q) => q.rank - p.rank)
    const boxes: [number, number, number, number][] = []
    const out: { name: string; position: [number, number, number] }[] = []
    for (const it of items) {
      const z = elevationAt(it.lon, it.lat) * exaggeration
      const [x, y] = viewport.project([it.lon, it.lat, z])
      if (x < 0 || y < 0 || x > size.width || y > size.height) continue
      const w = it.name.length * 7.5 + 12
      const box: [number, number, number, number] = [x - w / 2, y - 34, x + w / 2, y - 6]
      if (boxes.some((o) => !(box[2] < o[0] || box[0] > o[2] || box[3] < o[1] || box[1] > o[3]))) continue
      boxes.push(box)
      out.push({ name: it.name, position: [it.lon, it.lat, z] })
    }
    return out
  }, [m, terrain, viewport, elevationAt, exaggeration, size])

  // ------------------------------------------------------------ points (optional)

  const points = useMemo(() => {
    if (!pointMode || !m || !a || !terrain) return null
    const idx: number[] = []
    for (let i = 0; i < a.depth.length; i += 1) if (a.depth[i] >= WET_M) idx.push(i)
    const positions = new Float64Array(idx.length * 3)
    const colors = new Uint8Array(idx.length * 4)
    idx.forEach((cellIndex, n) => {
      const row = Math.floor(cellIndex / m.width)
      const col = cellIndex - row * m.width
      const [lon, lat] = cellLonLat(m.lattice, row, col)
      const e = terrain.elevation[cellIndex]
      positions.set([lon, lat, ((Number.isFinite(e) ? e : terrain.p2) + a.depth[cellIndex]) * exaggeration], n * 3)
      const [r, g, bl] = rampRgba(WATER_3D_RAMP, a.depth[cellIndex], WATER_3D.aboveTop)
      colors.set([r, g, bl, 235], n * 4)
    })
    return { length: idx.length, positions, colors }
  }, [pointMode, m, a, terrain, exaggeration])

  // ------------------------------------------------------------ layers

  const terrainMaterial = useMemo(() => ({ ambient: 0.35, diffuse: 0.6, shininess: 32, specularColor: [40, 38, 34] }), [])
  const waterMaterial = useMemo(() => ({ ambient: 0.4, diffuse: 0.55, shininess: 120, specularColor: [255, 248, 230] }), [])
  const layers = useMemo(() => {
    const built: unknown[] = []
    if (ground) built.push(new TerrainSurfaceLayer({ id: 'ground', mesh: ground, texture: null, scene, material: terrainMaterial }))
    if (shadedTerrain) {
      built.push(
        new TerrainSurfaceLayer({
          id: 'terrain',
          mesh: shadedTerrain,
          texture: imageryReady ? (imagery as Imagery).canvas : null,
          scene,
          material: terrainMaterial,
        }),
      )
    }
    if (water && !pointMode) built.push(new WaterSurfaceLayer({ id: 'water', mesh: water, scene, material: waterMaterial }))
    if (points && m) {
      built.push(
        new ScatterplotLayer({
          id: 'points',
          data: { length: points.length, attributes: { getPosition: { value: points.positions, size: 3 }, getFillColor: { value: points.colors, size: 4 } } },
          coordinateSystem: COORDINATE_SYSTEM.LNGLAT,
          getRadius: (m.cell_m * 0.5) as never,
          radiusUnits: 'meters',
          billboard: false,
          stroked: false,
          updateTriggers: { getPosition: [a?.t, exaggeration] },
        }),
      )
    }
    if (labels.length) {
      built.push(
        new ScatterplotLayer({
          id: 'label-dots',
          data: labels,
          getPosition: (d: (typeof labels)[number]) => d.position,
          getRadius: 4,
          radiusUnits: 'pixels',
          getFillColor: hexRgb(C.text),
          getLineColor: hexRgb(C.bg),
          lineWidthMinPixels: 1.5,
          stroked: true,
          parameters: { depthCompare: 'always' },
        }),
        new TextLayer({
          id: 'labels',
          data: labels,
          getPosition: (d: (typeof labels)[number]) => d.position,
          getText: (d: (typeof labels)[number]) => d.name,
          getSize: 13,
          sizeUnits: 'pixels',
          sizeMinPixels: 12,
          sizeMaxPixels: 14,
          billboard: true,
          getPixelOffset: [0, -12],
          getAlignmentBaseline: 'bottom',
          getColor: hexRgb(C.text),
          background: true,
          getBackgroundColor: [...hexRgb(C.bg), 210],
          backgroundPadding: [5, 3],
          fontFamily: 'Inter, system-ui, sans-serif',
          parameters: { depthCompare: 'always' },
        }),
      )
    }
    return built
  }, [ground, shadedTerrain, imagery, imageryReady, water, pointMode, points, m, labels, a, exaggeration, scene, terrainMaterial, waterMaterial])

  // ------------------------------------------------------------ hover

  const [hover, setHover] = useState<null | { x: number; y: number; row: number; col: number }>(null)
  const hoverFrame = useRef(0)
  /**
   * The cell under the cursor: march the view ray down from above the highest ground to below the lowest,
   * and take the first point where the drawn surface (ground, or water where wet) rises to meet it.
   */
  const onPointerMove = useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      const rect = event.currentTarget.getBoundingClientRect()
      const x = event.clientX - rect.left
      const y = event.clientY - rect.top
      cancelAnimationFrame(hoverFrame.current)
      hoverFrame.current = requestAnimationFrame(() => {
        if (!m || !terrain || !viewport || (event.target as HTMLElement).closest('.overlay')) {
          setHover(null)
          return
        }
        const surfaceAt = (lon: number, lat: number): [number, number, number] | null => {
          const [row, col] = lonLatCell(m.lattice, lon, lat)
          const r = Math.round(row)
          const c = Math.round(col)
          if (r < 0 || c < 0 || r >= m.height || c >= m.width) return null
          const i = r * m.width + c
          const e = terrain.elevation[i]
          const g = Number.isFinite(e) ? e : terrain.p2
          const d = a ? a.depth[i] : 0
          return [r, c, (g + (d >= WET_M ? d : 0)) * exaggeration]
        }
        const top = terrain.max * exaggeration + 50
        const bottom = terrain.min * exaggeration - 50
        const steps = 96
        let prev: { z: number; hit: [number, number, number] | null } | null = null
        for (let s = 0; s <= steps; s += 1) {
          const z = top + ((bottom - top) * s) / steps
          const [lon, lat] = viewport.unproject([x, y], { targetZ: z }) as number[]
          const hit = Number.isFinite(lon) && Number.isFinite(lat) ? surfaceAt(lon, lat) : null
          if (hit && hit[2] >= z) {
            // refine between the previous step and this one
            let lo = prev ? prev.z : z
            let hi = z
            let best = hit
            for (let k2 = 0; k2 < 12; k2 += 1) {
              const mid = (lo + hi) / 2
              const [ml, mt] = viewport.unproject([x, y], { targetZ: mid }) as number[]
              const h = surfaceAt(ml, mt)
              if (h && h[2] >= mid) {
                hi = mid
                best = h
              } else lo = mid
            }
            setHover({ x, y, row: best[0], col: best[1] })
            return
          }
          prev = { z, hit }
        }
        setHover(null)
      })
    },
    [m, terrain, viewport, a, exaggeration],
  )
  const near = Math.round(playhead)
  const nearFrame = near === k ? a : near === kB ? b : a
  const hoverValues = useMemo(() => {
    if (!hover || !m || !terrain) return null
    const i = hover.row * m.width + hover.col
    const ground = terrain.elevation[i]
    const depth = nearFrame ? nearFrame.depth[i] : Number.NaN
    const arr = arrival.data ? arrival.data[i] : Number.NaN
    return {
      ground: Number.isFinite(ground) ? ground : null,
      depth: depth >= WET_M ? depth : null,
      surface: Number.isFinite(ground) && depth >= WET_M ? ground + depth : null,
      arrival: Number.isFinite(arr) ? arr : null,
      frameTime: nearFrame?.timeS ?? null,
    }
  }, [hover, m, terrain, nearFrame, arrival.data])

  // ------------------------------------------------------------ readouts

  const timeS = m && frames ? m.times_s[k] + (kB > k ? (m.times_s[kB] - m.times_s[k]) * (playhead - k) : 0) : null
  const rampStops = WATER_3D_RAMP.filter(([v]) => v > 0)

  // ------------------------------------------------------------ render

  if (runs.isSuccess && !run) {
    return <div className="p-8 text-14 text-[var(--ink-text-dim)]">No completed run at this site.</div>
  }

  const legend = (
    <CollapsibleLegend title="Legend" testid="scene3d-legend">
      <div className="flex flex-col gap-1.5 text-12">
        <span className="section-label">Water depth (m)</span>
        <div className="flex">
          {rampStops.map(([v, c]) => (
            <div key={v} className="flex-1">
              <div style={{ height: 10, background: cssColour(c.slice(0, 7)) }} />
              <div className="tabular-nums text-[var(--ink-text-dim)]">{v}</div>
            </div>
          ))}
          {WATER_3D.aboveTop && (
            <div className="flex-1">
              <div style={{ height: 10, background: cssColour(WATER_3D.aboveTop.slice(0, 7)) }} />
              <div className="text-[var(--ink-text-dim)]">&gt;{rampStops[rampStops.length - 1]?.[0]}</div>
            </div>
          )}
        </div>
        <div className="text-[var(--ink-text-dim)]">
          {m?.has_vectors
            ? 'Streaks follow the stored depth-averaged velocity vectors (streak speed scaled for visibility).'
            : 'No velocity vectors stored: the surface shimmers, and no flow direction is drawn.'}
        </div>
        {plan && m && (
          <div className="text-[var(--ink-text-faint)]">
            Mesh {plan.nc} × {plan.nr} vertices (every {plan.stride} of {m.width} × {m.height} cells); readouts use every cell.
          </div>
        )}
        {terrain && terrain.voidCells > 0 && (
          <div className="text-[var(--ink-text-faint)]">
            {terrain.voidCells.toLocaleString('en-IN')} DEM void cells ({terrain.voidRule}) are filled from neighbours for drawing only.
          </div>
        )}
        <div className="text-[var(--ink-text-faint)]">
          Terrain: {terrain?.demSource ?? 'run DEM'} ·{' '}
          {imagery === 'loading' ? 'imagery loading' : imageryReady ? 'Esri World Imagery' : 'imagery unavailable: elevation tint (2nd–98th percentile)'}
        </div>
        <div className="text-[var(--ink-text-faint)]">Sun: 21 March, 09:30 local solar time; shadows traced over the relief as drawn.</div>
      </div>
    </CollapsibleLegend>
  )

  return (
    <div className="scene3d flex h-full min-h-0 flex-col" data-testid="scene3d-page">
      <div className="flex min-h-0 flex-1">
      {/* the side panel: everything that is not the scene */}
      <aside className="flex w-[268px] shrink-0 flex-col gap-4 overflow-y-auto border-r border-[var(--ink-border)] bg-[var(--ink-surface)] p-3" data-testid="scene3d-panel">
        {m && (
          <div data-testid="scene3d-model">
            <div className="section-label">Showing</div>
            <div className="text-14 text-[var(--ink-text)]">{m.label}</div>
            <div className="text-12 text-[var(--ink-text-dim)]">
              {run?.selected_model ? 'selected for this run on Comparison' : "this run's own result"}
            </div>
          </div>
        )}
        {siteRuns.length > 1 && (
          <label className="flex flex-col gap-1">
            <span className="section-label">Run</span>
            <select className="ctl w-full" value={run?.id ?? ''} onChange={(e) => setSelectedRunId(e.target.value)}>
              {siteRuns.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.id}
                </option>
              ))}
            </select>
          </label>
        )}
        <div>
          <div className="flex items-baseline justify-between">
            <span className="section-label">Vertical exaggeration</span>
            <span className="text-14 tabular-nums">×{exaggeration.toFixed(1)}</span>
          </div>
          <input
            type="range"
            min={1}
            max={6}
            step={0.1}
            value={exaggeration}
            onChange={(e) => setExaggeration(Number(e.target.value))}
            className="w-full"
            aria-label="Vertical exaggeration"
          />
        </div>
        <div>
          <span className="section-label">Camera</span>
          <div className="mt-1 grid grid-cols-2 gap-1">
            {presets.map((p) => (
              <button key={p.key} type="button" className="ctl truncate" title={p.label} onClick={() => (p.key === 'basin' ? reset() : flyTo(p.key === 'reach' ? framed(p.bounds, p.pitch) : closeView(p.bounds, p.pitch)))}>
                {p.label}
              </button>
            ))}
            <button type="button" className="ctl inline-flex items-center gap-1" onClick={reset} data-testid="scene3d-reset">
              <RotateCcw className="h-3.5 w-3.5" /> Reset view
            </button>
          </div>
          <div className="mt-1 text-12 text-[var(--ink-text-faint)]">Drag to orbit · right-drag to pan · wheel to zoom · arrows, + / −, R</div>
        </div>
        <label className="flex items-center gap-2 text-14">
          <input type="checkbox" checked={pointMode} onChange={() => setPointMode((v) => !v)} />
          Cells as points
        </label>
        <div data-testid="scene3d-hover">
          <div className="section-label mb-1">At the cursor</div>
          {hover && hoverValues ? (
            <div className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-0.5 text-12 tabular-nums">
              <span className="text-[var(--ink-text-dim)]">Terrain</span>
              <span>{hoverValues.ground === null ? 'DEM void' : `${hoverValues.ground.toFixed(1)} m`}</span>
              {hoverValues.depth !== null && (
                <>
                  <span className="text-[var(--ink-text-dim)]">Water depth</span>
                  <span>{hoverValues.depth.toFixed(2)} m</span>
                </>
              )}
              {hoverValues.surface !== null && (
                <>
                  <span className="text-[var(--ink-text-dim)]">Water surface</span>
                  <span>{hoverValues.surface.toFixed(1)} m</span>
                </>
              )}
              <span className="text-[var(--ink-text-dim)]">Arrival</span>
              <span>{hoverValues.arrival === null ? 'not reached' : clock(hoverValues.arrival)}</span>
              {hoverValues.frameTime !== null && (
                <>
                  <span className="text-[var(--ink-text-dim)]">Frame</span>
                  <span>{clock(hoverValues.frameTime)}</span>
                </>
              )}
              <span className="text-[var(--ink-text-dim)]">Cell</span>
              <span>
                r{hover.row} c{hover.col}
              </span>
            </div>
          ) : (
            <div className="text-12 text-[var(--ink-text-faint)]">Point at the terrain or the water.</div>
          )}
        </div>
        {legend}
      </aside>

      <main
        ref={mainRef}
        className="relative min-h-0 min-w-0 flex-1 overflow-hidden"
        onMouseLeave={() => setHover(null)}
        onPointerMove={onPointerMove}
        style={{ background: `linear-gradient(180deg, var(--ink-sky-top) 0%, var(--ink-sky-horizon) 100%)` }}
      >
        {view && (
          <DeckGL
            ref={deckRef}
            views={VIEW}
            viewState={view as never}
            onViewStateChange={({ viewState }) => {
              setHover(null)
              setView(clampView(viewState as unknown as ViewState))
            }}
            controller={CONTROLLER as never}
            effects={effects}
            layers={layers as never}
            getCursor={({ isDragging }) => (isDragging ? 'grabbing' : 'crosshair')}
            onError={(error: Error) => {
              if (error?.name !== 'AbortError') console.error(error)
            }}
          />
        )}
        {(!view || !terrainMesh) && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className="overlay px-4 py-2 text-14">Loading terrain{imagery === 'loading' ? ' and imagery' : ''}…</div>
          </div>
        )}
        {/* the one chrome cluster: north, scale, the vertical stretch, attribution */}
        <CornerChrome
          bearing={view?.bearing ?? 0}
          metresPerPixel={viewport ? (viewport as unknown as { metersPerPixel: number }).metersPerPixel : null}
          badge={`Vertical ×${exaggeration.toFixed(1)}`}
          attribution={imageryReady ? IMAGERY_ATTRIBUTION : undefined}
        />
      </main>
      </div>

      {/* timeline */}
      <footer className="flex shrink-0 items-center gap-3 border-t border-[var(--ink-border)] bg-[var(--ink-surface)] px-4 py-2">
        <button className="ctl" type="button" onClick={togglePlay} disabled={frames < 2} aria-label={playing ? 'Pause' : 'Play'} data-testid="scene3d-play">
          {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
        </button>
        <input
          type="range"
          min={0}
          max={Math.max(0, frames - 1)}
          step={0.01}
          value={playhead}
          onPointerDown={() => {
            live.current.scrubbing = true
          }}
          onChange={(e) => {
            playheadRef.current = Number(e.target.value)
            setPlayhead(Number(e.target.value))
          }}
          onPointerUp={() => {
            // playing carries on from where the scrub let go; paused rests on a stored frame
            live.current.scrubbing = false
            if (!live.current.playing) {
              const r = Math.round(playheadRef.current)
              playheadRef.current = r
              setPlayhead(r)
            }
          }}
          onKeyDown={(e) => {
            if (e.code === 'Space') {
              e.preventDefault()
              togglePlay()
            }
          }}
          data-testid="scene3d-timeline"
          className="min-w-0 flex-1"
          disabled={frames < 2}
          aria-label="Timeline"
        />
        {timeS !== null && (
          <span className="w-[112px] text-right font-mono text-16 tabular-nums" data-testid="scene3d-clock">
            {clock(timeS).replace(/^T([+−])/, 'T $1 ')}
          </span>
        )}
        {frames > 0 && (
          <span className="w-[112px] font-mono text-12 tabular-nums text-[var(--ink-text-dim)]" data-testid="scene3d-frame">
            frame {Math.round(playhead)} / {frames - 1}
          </span>
        )}
        <div className="flex gap-1">
          {SPEEDS.map((s) => (
            <button key={s} type="button" className="ctl tabular-nums" data-active={speed === s} onClick={() => setSpeed(s)}>
              {s}×
            </button>
          ))}
        </div>
        <Link to="/compare-models" className="ctl">
          Change model
        </Link>
      </footer>
    </div>
  )
}
